import { useState, useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/AuthContext';

export interface RankingStatRow {
  id: string;
  sport: string;
  category: string;
  subcategory: string;
  total_points: number;
  tournaments_played: number | null;
  times_qf: number;
  times_sf: number;
  times_subcampeon: number;
  times_champion: number;
  tournaments_circuito: number | null;
  tournaments_relampago: number | null;
  circuito_qf: number;
  circuito_sf: number;
  circuito_subcampeon: number;
  circuito_champion: number;
  circuito_points: number;
  relampago_qf: number;
  relampago_sf: number;
  relampago_subcampeon: number;
  relampago_champion: number;
  relampago_points: number;
}

export function usePlayerRankingStats(userId?: string) {
  const { user: authUser } = useAuth();
  const targetUserId = userId || authUser?.id;
  const [stats, setStats] = useState<RankingStatRow[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!targetUserId) {
      setLoading(false);
      return;
    }

    const fetch = async () => {
      setLoading(true);
      const { data, error } = await supabase
        .from('ranking_aggregate')
        .select('id, sport, category, subcategory, total_points, tournaments_played, times_qf, times_sf, times_subcampeon, times_champion, tournaments_circuito, tournaments_relampago, circuito_qf, circuito_sf, circuito_subcampeon, circuito_champion, circuito_points, relampago_qf, relampago_sf, relampago_subcampeon, relampago_champion, relampago_points')
        .eq('user_id', targetUserId);

      if (!error && data) {
        const normalize = (s: string) =>
          s.toLowerCase().normalize('NFD').replace(/[\u0300-\u036f]/g, '');

        const canonicalSports: Record<string, string> = {
          'padel': 'Padel',
          'tenis': 'Tenis',
          'pickleball': 'Pickleball',
        };

        const grouped: Record<string, RankingStatRow> = {};
        for (const row of data as RankingStatRow[]) {
          const key = `${normalize(row.sport)}|${row.category}|${row.subcategory}`;
          if (!grouped[key]) {
            grouped[key] = { ...row };
            const canonical = canonicalSports[normalize(row.sport)];
            if (canonical) grouped[key].sport = canonical;
          } else {
            const g = grouped[key];
            g.total_points += row.total_points;
            g.tournaments_played = (g.tournaments_played || 0) + (row.tournaments_played || 0);
            g.tournaments_circuito = (g.tournaments_circuito || 0) + (row.tournaments_circuito || 0);
            g.tournaments_relampago = (g.tournaments_relampago || 0) + (row.tournaments_relampago || 0);
            g.times_qf += row.times_qf;
            g.times_sf += row.times_sf;
            g.times_subcampeon += row.times_subcampeon;
            g.times_champion += row.times_champion;
            g.circuito_qf += row.circuito_qf;
            g.circuito_sf += row.circuito_sf;
            g.circuito_subcampeon += row.circuito_subcampeon;
            g.circuito_champion += row.circuito_champion;
            g.circuito_points += row.circuito_points;
            g.relampago_qf += row.relampago_qf;
            g.relampago_sf += row.relampago_sf;
            g.relampago_subcampeon += row.relampago_subcampeon;
            g.relampago_champion += row.relampago_champion;
            g.relampago_points += row.relampago_points;
          }
        }
        setStats(Object.values(grouped));
      }
      setLoading(false);
    };

    fetch();
  }, [targetUserId]);

  return { stats, loading };
}
